/**
 * Utility functions to resolve image paths from filenames.
 * All images follow the convention: /assets/scenes/{sceneId}/{filename}
 */

import type { ChoiceCondition, ConditionalBg } from '../features/scenes/types'
import { IMAGE_SIZES, TEXTURE_SIZES } from '../generated/image-sizes'

const dataBase: string = import.meta.env.VITE_DATA_BASE ?? '/'

/**
 * Prepend data-package base path so asset URLs resolve correctly on both dev and prod.
 * Dev: '/' (assets served from public/)
 * Prod: '/cms/rychle-sipy-a-velikonocni-poklad-data/' (separate data package)
 */
export function assetUrl(path: string): string {
  const clean = path.startsWith('/') ? path.slice(1) : path
  return `${dataBase}${clean}`
}

// ---------------------------------------------------------------------------
// Responsive image helpers
// ---------------------------------------------------------------------------

const HALF_SUFFIX = '-half'

/**
 * Derive the half-size URL from a full-size image URL.
 * E.g. `/assets/scenes/foo/bg.png` → `/assets/scenes/foo/bg-half.png`
 */
export function toHalfSizeUrl(url: string): string {
  const dotIdx = url.lastIndexOf('.')
  if (dotIdx === -1) return url
  return url.slice(0, dotIdx) + HALF_SUFFIX + url.slice(dotIdx)
}

/**
 * Extract the asset-relative key from a full URL.
 * `/assets/scenes/foo/bg.png` → `scenes/foo/bg.png`
 * `/cms/.../assets/scenes/foo/bg.png` → `scenes/foo/bg.png`
 */
function toAssetKey(url: string): string | null {
  const idx = url.indexOf('/assets/')
  if (idx === -1) return null
  return url.slice(idx + '/assets/'.length)
}

/**
 * Look up the generated size data for an image URL.
 * Returns null if the image has no half-size variant.
 */
function getImageSizeData(url: string): { full: number; half: number } | null {
  const key = toAssetKey(url)
  if (!key) return null
  return IMAGE_SIZES[key] ?? null
}

/**
 * Build the `srcset` attribute value for an image URL that has a half-size variant.
 * Uses actual pixel widths from the generated lookup.
 * Returns empty string if the URL doesn't have size data.
 */
export function buildSrcset(fullUrl: string): string {
  const sizes = getImageSizeData(fullUrl)
  if (!sizes) return ''
  return `${toHalfSizeUrl(fullUrl)} ${sizes.half}w, ${fullUrl} ${sizes.full}w`
}

/**
 * The `sizes` attribute for scene images, matching the CSS layout:
 * - ≥1280px (desktop):  scene container is max 800px
 * - ≥1054px:            scene image is max 1000px
 * - below:              scene image fills ~95% of viewport
 */
export const SCENE_IMAGE_SIZES = '(min-width: 1280px) 800px, (min-width: 1054px) 1000px, 95vw'

/**
 * Detect a slow network via the Network Information API.
 * Returns true on 2g/slow-2g, or when the user enabled data-saver,
 * or when estimated downlink is below 1 Mbps.
 */
export function isSlowNetwork(): boolean {
  const conn = (navigator as any).connection as
    | { saveData?: boolean; effectiveType?: string; downlink?: number }
    | undefined
  if (!conn) return false
  if (conn.saveData) return true
  if (conn.effectiveType === '2g' || conn.effectiveType === 'slow-2g') return true
  if (typeof conn.downlink === 'number' && conn.downlink < 1) return true
  return false
}

/**
 * Apply srcset + sizes to an `<img>` element if it qualifies for a half-size variant.
 * On very slow networks, forces the half-size as the primary src.
 */
export function applyResponsiveImage(img: HTMLImageElement): void {
  const src = img.getAttribute('src') || img.src || ''
  const srcset = buildSrcset(src)
  if (!srcset) return

  if (isSlowNetwork()) {
    img.src = toHalfSizeUrl(src)
    return
  }

  img.srcset = srcset
  img.sizes = SCENE_IMAGE_SIZES
}

/**
 * Pick the smallest texture variant whose width ≥ the viewport's physical pixel width.
 * Falls back to the original (largest) when no variant is wide enough.
 */
export function resolveTextureUrl(filename: string): string {
  const key = `images/${filename}`
  const variants = TEXTURE_SIZES[key]
  if (!variants || variants.length === 0) return assetUrl(`/assets/images/${filename}`)

  const physicalWidth = window.innerWidth * (window.devicePixelRatio || 1)
  const ext = filename.slice(filename.lastIndexOf('.'))
  const base = filename.slice(0, filename.lastIndexOf('.'))

  for (let i = variants.length - 1; i >= 0; i--) {
    if (variants[i].w >= physicalWidth) {
      const suffix = variants[i].s
      if (!suffix) return assetUrl(`/assets/images/${filename}`)
      return assetUrl(`/assets/images/${base}${suffix}${ext}`)
    }
  }
  return assetUrl(`/assets/images/${filename}`)
}

export function getSceneImagePath(sceneId: string, filename: string): string {
  return assetUrl(`/assets/scenes/${sceneId}/${filename}`)
}

export function getBubbleImagePath(sceneId: string, bubbleId: string): string {
  const match = bubbleId.match(/bubble-(\d+)/)
  const num = match ? match[1] : bubbleId.replace('bubble-', '')
  return assetUrl(`/assets/scenes/${sceneId}/bubble-${num}.png`)
}

export function getChoiceImagePath(sceneId: string, choiceNum: number): string {
  return assetUrl(`/assets/scenes/${sceneId}/choice-${choiceNum}.png`)
}

export function getChoiceBgPath(choiceNum: number): string {
  return assetUrl(`/assets/images/choice-${choiceNum}-bg.png`)
}

export function getChoiceBorderPath(choiceNum: number): string {
  return assetUrl(`/assets/images/choice-${choiceNum}-border.png`)
}

export function getChoiceBorderActivePath(choiceNum: number): string {
  return assetUrl(`/assets/images/choice-${choiceNum}-border-active.png`)
}

export function getDefaultBgPath(sceneId: string): string {
  return assetUrl(`/assets/scenes/${sceneId}/bg.png`)
}

/**
 * Resolve the background image path for a scene.
 * Handles string (static), false (none), and ConditionalBg[] (first matching `when` wins).
 * Returns null when the scene explicitly opts out of a background.
 */
export function resolveSceneBg(
  scene: { id: string; bg?: string | false | ConditionalBg[]; type?: string },
  evaluateCondition?: (condition: ChoiceCondition) => boolean,
): string | null {
  if (scene.bg === false) return null
  if (typeof scene.bg === 'string') return scene.bg

  if (Array.isArray(scene.bg)) {
    for (const entry of scene.bg) {
      if (!entry.when) return getSceneImagePath(scene.id, entry.src)
      if (evaluateCondition && evaluateCondition(entry.when)) {
        return getSceneImagePath(scene.id, entry.src)
      }
    }
    return null
  }

  return getDefaultBgPath(scene.id)
}

/**
 * Collect all possible bg image URLs from a conditional bg array (for preloading).
 */
export function collectConditionalBgUrls(sceneId: string, bg: ConditionalBg[]): string[] {
  return bg.map((entry) => getSceneImagePath(sceneId, entry.src))
}
